<!--
  SettlementMonthPicker.vue
  콜봇 월 정산 · 월 선택 — `이번 달` / `지난 달` 버튼 + `월 선택` 피커(month), 진행 중인 달 표시.
  목록 헤더(화면 명세 1), 병원 상세 제목 줄(화면 명세 2 공통 헤더), 기본 요율 화면의 자동 마감 요약
  대상 달(화면 명세 3)이 같이 쓴다.

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 1 헤더" — 이번 달을 고르면 "진행 중인 달"
  배지(사용량이 아직 늘어난다). 월은 KST 'YYYY-MM' 문자열로만 다룬다(settlementFormat.js).

  - props: value('YYYY-MM', 필수) · showInProgress(기본 true) · disabledFuture(기본 true — 다음 달부터 못 고름)
  - emits: change(month) — 지금 값과 다를 때만. 라우트 쿼리 반영은 부모 몫이다.
  - 버튼은 고른 달이 이번 달·지난 달이면 켜진다. 그 밖의 달은 피커 테두리가 켜진다.
-->
<template>
  <div class="month-picker" role="group" aria-label="정산 월">
    <div class="month-picker__quick">
      <button
        v-for="opt in quickOptions"
        :key="opt.key"
        type="button"
        class="month-picker__btn"
        :class="{ 'is-active': value === opt.month }"
        :aria-pressed="value === opt.month ? 'true' : 'false'"
        @click="pick(opt.month)"
      >
        {{ opt.label }}
      </button>
    </div>

    <a-date-picker
      class="month-picker__picker"
      :class="{ 'is-active': isOtherMonth }"
      :value="pickerValue"
      picker="month"
      value-format="YYYY-MM"
      format="YYYY년 M월"
      placeholder="월 선택"
      :locale="locale"
      :allow-clear="false"
      :input-read-only="true"
      :disabled-date="disabledDate"
      aria-label="월 선택"
      @change="onPick"
    />

    <a-tooltip v-if="showLive" title="사용량이 아직 늘어납니다">
      <a-tag color="processing" class="month-picker__live">진행 중인 달</a-tag>
    </a-tooltip>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import locale from 'ant-design-vue/es/date-picker/locale/ko_KR';
import 'dayjs/locale/ko';

import { currentMonthKst, isValidMonth, previousMonthKst } from '../service/settlementFormat';

const props = defineProps({
  /** 고른 달 'YYYY-MM'(KST) */
  value: { type: String, required: true },
  /** 이번 달이면 "진행 중인 달" 배지 */
  showInProgress: { type: Boolean, default: true },
  /** 다음 달부터는 고르지 못하게 */
  disabledFuture: { type: Boolean, default: true },
});

const emit = defineEmits(['change']);

// 페이지를 연 시점의 KST 달 — 열어 둔 채 달이 바뀌는 경우는 새로고침으로 맞춘다
const thisMonth = currentMonthKst();
const lastMonth = previousMonthKst();

const quickOptions = [
  { key: 'current', label: '이번 달', month: thisMonth },
  { key: 'previous', label: '지난 달', month: lastMonth },
];

const pickerValue = computed(() => (isValidMonth(props.value) ? props.value : null));
const isOtherMonth = computed(
  () => pickerValue.value !== null && props.value !== thisMonth && props.value !== lastMonth
);
const showLive = computed(() => props.showInProgress && props.value === thisMonth);

/** 피커의 달 칸(dayjs) — 이번 KST 달보다 뒤면 막는다 */
function disabledDate(current) {
  if (!props.disabledFuture || !current) return false;
  return current.format('YYYY-MM') > thisMonth;
}

function pick(month) {
  if (isValidMonth(month) && month !== props.value) emit('change', month);
}

// value-format 이 'YYYY-MM' 이라 첫 인자가 그 문자열이다
function onPick(month) {
  pick(month);
}
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

.month-picker {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  &__quick {
    display: inline-flex;
    height: 32px;
    padding: 2px;
    border: 1px solid $st-border-strong;
    border-radius: 8px;
    background: #fff;
    box-sizing: border-box;
  }

  &__btn {
    height: auto; // 전역 button { height: 40px } 덮어쓰기 — 없으면 32px 묶음 밖으로 넘친다
    padding: 0 12px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: $st-text-secondary;
    font-size: 13px;
    font-weight: 500;
    line-height: 26px;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      color: $st-text;
    }

    &:focus-visible {
      outline: 2px solid $st-accent;
      outline-offset: 1px;
    }

    &.is-active {
      background: $st-accent-bg;
      color: $st-accent;
      font-weight: 600;
    }
  }

  &__picker {
    width: 140px;

    &.is-active {
      border-color: $st-accent;
      background: $st-accent-bg;
    }
  }

  &__live {
    margin-inline-end: 0;
    cursor: help;
  }
}
</style>
